import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { logAuth } from "./debug-log";
import {
  type AuthResult,
  getCurrentSession,
  sendMagicLink,
  signOut as signOutHelper,
} from "./magic-link";
import { ensureProfileForSession } from "./session-profile";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  client: SupabaseClient;
  signIn: (email: string, options?: { redirectTo?: string }) => Promise<AuthResult>;
  signOut: () => Promise<AuthResult>;
}

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  client: SupabaseClient;
  children: ReactNode;
}

export function AuthProvider({ client, children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  // Compte dont le profil est déjà garanti (ou en cours de garantie), pour
  // n'appeler le serveur qu'une fois par session : `onAuthStateChange` répète
  // la même session à chaque INITIAL_SESSION / TOKEN_REFRESHED (~horaire).
  const profileEnsuredFor = useRef<string | null>(null);

  // Garantit le profil (donc le projet personnel) dès qu'une session existe,
  // et non plus au premier envoi réseau. Volontairement PAS attendu : l'app
  // reste utilisable pendant l'appel, et un échec réseau ne bloque aucun rendu.
  const guaranteeProfile = useCallback(
    (current: Session | null) => {
      if (!current) {
        // Déconnexion : le prochain compte devra garantir le sien.
        profileEnsuredFor.current = null;
        return;
      }
      const userId = current.user.id;
      if (profileEnsuredFor.current === userId) return;
      profileEnsuredFor.current = userId;
      ensureProfileForSession(client, { id: userId, email: current.user.email }).then((ok) => {
        // Un échec (hors ligne au premier lancement) n'est pas mis en cache :
        // le prochain événement d'authentification retentera.
        if (!ok && profileEnsuredFor.current === userId) profileEnsuredFor.current = null;
      });
    },
    [client],
  );

  useEffect(() => {
    let isMounted = true;
    logAuth("info", "mount → getCurrentSession");

    getCurrentSession(client).then((result) => {
      if (!isMounted) return;
      if (result.ok) {
        logAuth(
          "info",
          "getCurrentSession ok",
          result.data ? { user: result.data.user.email } : "no-session",
        );
        setSession(result.data);
        setStatus(result.data ? "authenticated" : "unauthenticated");
        guaranteeProfile(result.data);
      } else {
        logAuth("warn", "getCurrentSession error", result.error);
        setStatus("unauthenticated");
      }
    });

    const { data } = client.auth.onAuthStateChange((event, newSession) => {
      logAuth(
        "info",
        `onAuthStateChange ${event}`,
        newSession ? { user: newSession.user.email } : "no-session",
      );
      setSession(newSession);
      setStatus(newSession ? "authenticated" : "unauthenticated");
      guaranteeProfile(newSession);
    });

    return () => {
      isMounted = false;
      data.subscription.unsubscribe();
    };
  }, [client, guaranteeProfile]);

  const signIn = useCallback(
    async (email: string, options?: { redirectTo?: string }) => {
      logAuth("info", "signIn → sendMagicLink", { email, redirectTo: options?.redirectTo });
      const r = await sendMagicLink(client, email, options);
      logAuth("info", "signIn result", r.ok ? "ok" : { error: r.error });
      return r;
    },
    [client],
  );

  const signOut = useCallback(async () => {
    logAuth("info", "signOut");
    const r = await signOutHelper(client);
    logAuth("info", "signOut result", r.ok ? "ok" : { error: r.error });
    return r;
  }, [client]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      session,
      user: session?.user ?? null,
      client,
      signIn,
      signOut,
    }),
    [status, session, client, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth doit être utilisé à l'intérieur d'un AuthProvider");
  }
  return ctx;
}
